import React, { useState } from "react";
import { Button, Flex } from "@jsq/ds/core";
import {
  ReturnToFundAdminModal,
  getRejectionReasonLabel,
} from "./modals/ReturnToFundAdminModal";
import { usePostBtsComment } from "./usePostBtsComment";
import type { TaskStepDetail } from "../../types";

interface ExternalActionBarProps {
  task: TaskStepDetail;
  actionLoading: boolean;
  /** True when task is unassigned or assigned to another user. Locks Approve / Return. */
  isNotAssignee?: boolean;
  onActionClick: (action: string, payload?: Record<string, unknown>) => void;
  /** JSM issue key used to post the return note as a BTS comment alongside the action. */
  issueKey?: string;
}

// Action bar for the GP external-review surface (ExecutionState.WAITING). The
// external reviewer can approve the step or return it to the fund admin with a
// structured rejection reason. The "Return to Fund Admin" reject path always
// opens the reason modal so a coded reason is captured before the mutation.
export const ExternalActionBar: React.FC<ExternalActionBarProps> = ({
  task,
  actionLoading,
  isNotAssignee = false,
  onActionClick,
  issueKey,
}) => {
  const postComment = usePostBtsComment(issueKey, task);
  const [returnOpen, setReturnOpen] = useState(false);

  // Close the modal when navigating to a different task — same derived-state-
  // during-render pattern OnBehalfActionBar uses.
  const [prevTaskId, setPrevTaskId] = useState(task.id);
  if (prevTaskId !== task.id) {
    setPrevTaskId(task.id);
    setReturnOpen(false);
  }

  const canApprove = task.availableActions.some((a) => a.action === "approve");
  const canReject = task.availableActions.some((a) => a.action === "reject");

  const handleReturnConfirm = (rejectionReason: string, note: string) => {
    setReturnOpen(false);
    onActionClick("reject", { rejectionReason, note });
    const reasonLabel = getRejectionReasonLabel(rejectionReason);
    const commentText = note.trim() ? `${reasonLabel} : ${note}` : reasonLabel;
    postComment(commentText, false);
  };

  if (!canApprove && !canReject) return null;

  return (
    <>
      <Flex gap="8px" alignItems="center">
        {canReject && (
          <Button
            variant="neutral"
            disabled={actionLoading || isNotAssignee}
            onClick={() => setReturnOpen(true)}
            data-testid="external-return-to-fund-admin"
          >
            Return to Fund Admin
          </Button>
        )}
        {canApprove && (
          <Button
            variant="primary"
            disabled={actionLoading || isNotAssignee}
            onClick={() => onActionClick("approve")}
            data-testid="external-approve"
          >
            Approve
          </Button>
        )}
      </Flex>
      <ReturnToFundAdminModal
        isOpen={returnOpen}
        loading={actionLoading}
        onCancel={() => setReturnOpen(false)}
        onConfirm={handleReturnConfirm}
      />
    </>
  );
};

export default ExternalActionBar;
